import { useEffect, type ComponentProps } from "react";
import { Button, PocketRow, PocketSection, StatusDot } from "@/components/ui";
import { useAuth } from "@/hooks/use-auth";
import { useT } from "@/i18n";
import { settingsShellMessages } from "@/i18n/messages/settings-shell";
import { trayProfileInitials } from "@/desktop/tray-profile-initials";
import { accountPlan, useSubscriptionStatus } from "./account-plan-state";
import {
  SETTINGS_ROOT_GROUPS,
  settingsPageDef,
  useSettingsNav,
  type SettingsPageDef,
  type SettingsPageId,
} from "./settings-navigation";

import {
  attentionStatus,
  useAppAttentionCount,
  useRuntimeAttentionCount,
} from "./connections-state";
import { useMemoryReviewCount } from "./memory-review-state";
import { useProjectStore } from "@/stores/project-store";
import { useIntegrationStore } from "@/stores/integration-store";
import { COMPOSIO_UI_ENABLED } from "@/lib/composio";

type RootRowValue = Pick<
  ComponentProps<typeof PocketRow>,
  "trailing" | "status"
>;

/** Settings root: the profile block, then one row per root page (spec §3). */
export function SettingsRootPage() {
  const nav = useSettingsNav();
  const t = useT(settingsShellMessages);
  const { user } = useAuth();
  const { status } = useSubscriptionStatus();
  const apps = useAppAttentionCount();
  const runtimes = useRuntimeAttentionCount();
  const memoryCount = useMemoryReviewCount(status?.has_pro_access === true);
  const projects = useProjectStore((state) => state.projects);
  useEffect(() => {
    if (COMPOSIO_UI_ENABLED)
      void useIntegrationStore.getState().actions.refreshConnections();
  }, [user?.id]);
  const plan = status ? accountPlan(status) : null;

  function rootRowProps(page: SettingsPageId): RootRowValue {
    if (page === "account" && plan?.paymentFailed)
      return { status: { tone: "error", text: t("paymentFailed") } };
    if (page === "preferences" && runtimes)
      return { status: attentionStatus(runtimes) };
    if (page === "plugins" && apps) return { status: attentionStatus(apps) };
    if (page === "memory" && memoryCount)
      return { trailing: t("memoryToReview", { count: memoryCount }) };
    if (page === "projects") return { trailing: String(projects.length) };
    return {};
  }

  return (
    <div data-testid="settings-page-root">
      <div
        className="flex items-center gap-2.5 px-2 pb-3 pt-0.5"
        data-testid="settings-profile-block"
      >
        <span
          aria-hidden="true"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--color-surface-selected)] text-appkit-small font-semibold text-primary"
          data-testid="settings-profile-initials"
        >
          {trayProfileInitials(user?.name, user?.email)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-appkit-body font-semibold text-primary">
            {user?.name || "Denker"}
          </p>
          <p
            className="flex items-center gap-1.5 truncate text-appkit-small font-medium text-muted"
            data-testid="settings-profile-line"
          >
            {plan?.paymentFailed ? <StatusDot tone="error" /> : null}
            <span className="truncate">{plan?.planLine ?? user?.email}</span>
          </p>
        </div>
        {plan?.free ? (
          <Button
            size="sm"
            onClick={() => nav.push({ page: "account" })}
            data-testid="settings-upgrade"
          >
            {t("upgrade")}
          </Button>
        ) : null}
      </div>
      {SETTINGS_ROOT_GROUPS.map((group) => (
        <PocketSection
          key={group[0]}
          testId={`settings-root-${group[0]}-group`}
        >
          {group
            .map((page) => settingsPageDef(page))
            .filter((def): def is SettingsPageDef => Boolean(def))
            .map((def) => {
              const Icon = def.icon;
              return (
                <PocketRow
                  key={def.id}
                  testId={`settings-row-${def.id}`}
                  leading={
                    Icon ? (
                      <Icon className="h-4 w-4 text-muted" aria-hidden="true" />
                    ) : undefined
                  }
                  title={def.title}
                  chevron
                  onSelect={() => nav.push({ page: def.id })}
                  {...rootRowProps(def.id)}
                />
              );
            })}
        </PocketSection>
      ))}
    </div>
  );
}
